import { useState, useMemo } from "react";
import { componentRegistry } from "./registry";

const ComponentsCatalog = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [copied, setCopied] = useState(null);

  const categories = useMemo(
    () => ["All", ...new Set(componentRegistry.map((c) => c.category))],
    []
  );

  const filtered = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return componentRegistry.filter((component) => {
      const matchesSearch =
        !query ||
        component.name.toLowerCase().includes(query) ||
        component.description.toLowerCase().includes(query);
      const matchesCategory =
        selectedCategory === "All" || component.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [searchQuery, selectedCategory]);

  const copyCode = async (code, id) => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error("Copy failed:", err);
    }
  };

  return (
    <div className="w-full space-y-6">
      {/* Header */}
      <div className="rounded-xl border border-slate-700 bg-gradient-to-r from-slate-900 to-slate-950 p-6">
        <h1 className="text-4xl font-bold text-slate-100 mb-2">
          Component Catalog
        </h1>
        <p className="text-slate-400 mb-6">
          {componentRegistry.length} components - Copy Tailwind code and use directly
        </p>

        {/* Search & Filter */}
        <div className="flex flex-col md:flex-row gap-3">
          <input
            type="text"
            placeholder="Search components..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="flex-1 px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 placeholder-slate-500 focus:border-amber-500 outline-none transition"
          />
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="px-4 py-2 bg-slate-800 border border-slate-600 rounded-lg text-slate-200 focus:border-amber-500 outline-none transition"
          >
            {categories.map((cat) => (
              <option key={cat} value={cat}>
                {cat}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Results */}
      <div className="text-sm text-slate-400 mb-4">
        Showing {filtered.length} of {componentRegistry.length} components
      </div>

      {/* Components Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filtered.map((component) => {
          const Component = component.Component;
          const code = `import ${component.name} from "@/components/${component.name}";

export default function Page() {
  return <${component.name} />;
}`;

          return (
            <div
              key={component.id}
              className="border border-slate-700 rounded-xl overflow-hidden bg-slate-900/50 hover:bg-slate-900 transition"
            >
              {/* Component Name */}
              <div className="px-4 py-3 border-b border-slate-700 bg-slate-950">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-semibold text-slate-100">
                    {component.id}. {component.name}
                  </h3>
                  <span className="text-xs px-2 py-1 rounded bg-amber-500/20 text-amber-300 border border-amber-500/30">
                    {component.category}
                  </span>
                </div>
                <p className="text-xs text-slate-400">{component.description}</p>
              </div>

              {/* Preview */}
              <div className="p-4 bg-gradient-to-b from-slate-900 to-slate-950">
                <div className="rounded-lg border border-slate-700 bg-slate-950/50 p-6 flex items-center justify-center min-h-[160px]">
                  <Component />
                </div>
              </div>

              {/* Code */}
              <div className="px-4 py-3 border-t border-slate-700 bg-slate-950">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-mono text-slate-400">JSX</span>
                  <button
                    onClick={() => copyCode(code, component.id)}
                    className={`text-xs px-3 py-1 rounded transition ${
                      copied === component.id
                        ? "bg-green-500/30 text-green-300 border border-green-500/50"
                        : "bg-slate-800 text-slate-300 border border-slate-600 hover:border-amber-400"
                    }`}
                  >
                    {copied === component.id ? "Copied" : "Copy Code"}
                  </button>
                </div>
                <pre className="text-xs text-slate-300 overflow-x-auto bg-slate-950 p-3 rounded border border-slate-700 font-mono">
                  <code>{code}</code>
                </pre>
              </div>
            </div>
          );
        })}
      </div>

      {filtered.length === 0 && (
        <div className="text-center py-12">
          <p className="text-slate-400">No components found</p>
        </div>
      )}
    </div>
  );
};

export default ComponentsCatalog;


